import {
    Features,
    getFeatureFlagValue,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    defaultLanguage,
    getCachedLanguage,
    Languages,
    setLanguage,
} from '@uipath/portal-shell-util';
import { useMemo } from 'react';

import type { LanguageCulture } from './language';
import { languageCodes } from './language';

export const processLocaleKeys = (
    enableLocKeysLanguage: boolean,
    setStringsCallback: (strings: Record<string, string>) => void,
    en: {},
) => {
    if (!enableLocKeysLanguage) {
        return;
    }

    const keys = Object.keys(en).reduce(
        (accum, current) => ({
            ...accum,
            [current]: current,
        }),
        {},
    );
    setStringsCallback(keys);
};

export function isValidLanguageCode(languageCode: string | null | undefined) {
    return languageCodes.find(language => language.toLowerCase() === languageCode?.toLowerCase());
}

export function getUserLanguageFromLocalStorage(fallbackLang?: string) {
    const EnableLocKeysLanguage = getFeatureFlagValue(Features.EnableLocKeysLanguage.name);
    const EnableLocRoLanguage = getFeatureFlagValue(Features.EnableLocRoLanguage.name);

    const fallbackLanguage = isValidLanguageCode(fallbackLang) ?? getBrowserLanguage();
    const languageFromLocalStorage = isValidLanguageCode(getCachedLanguage());

    return isValidLanguageCode(
        !languageFromLocalStorage
        || (!EnableLocKeysLanguage && languageFromLocalStorage?.toLowerCase() === 'keys')
        || (!EnableLocRoLanguage && languageFromLocalStorage?.toLowerCase() === 'ro')
            ? fallbackLanguage
            : languageFromLocalStorage,
    ) ?? fallbackLanguage as LanguageCulture;
}

export const getBrowserLanguage = () =>
    isValidLanguageCode(navigator.language) ?? isValidLanguageCode(navigator.language.substring(0, 2)) ?? defaultLanguage;

export function setUserLanguageInLocalStorage(language: string) {
    const validLanguage = isValidLanguageCode(language);
    if (validLanguage) {
        setLanguage(validLanguage);
    }
    return validLanguage;
}

export function useSupportedLanguagesMap() {
    const EnableLocKeysLanguage = useFeatureFlagValue(Features.EnableLocKeysLanguage.name);
    const EnableLocRoLanguage = useFeatureFlagValue(Features.EnableLocRoLanguage.name);

    return useMemo(() => {
        const languagesMap: { [key: string]: string } = { ...Languages };
        if (!EnableLocKeysLanguage) {
            delete languagesMap['keys'];
        }
        if (!EnableLocRoLanguage) {
            delete languagesMap['ro'];
        }
        return languagesMap;
    }, [ EnableLocKeysLanguage, EnableLocRoLanguage ]);
}

export function getLanguageForDocLinks(lang: LanguageCulture) {
    switch (lang) {
        case 'en':
        case 'keys':
            return '';
        case 'zh-CN':
        case 'zh-TW':
            return `lang-zh_CN/`;
        case 'es-MX':
            return `lang-es_MX/`;
        case 'pt-BR':
            return `lang-pt_BR/`;
        default:
            return `lang-${lang}/`;
    }
}

export function useSupportedLanguages() {
    const EnableLocKeysLanguage = useFeatureFlagValue(Features.EnableLocKeysLanguage.name);
    const EnableLocRoLanguage = useFeatureFlagValue(Features.EnableLocRoLanguage.name);

    return useMemo(() => {
        let languageList = Object.entries(Languages);
        if (!EnableLocKeysLanguage) {
            languageList = languageList.filter(lang => lang[0] !== 'keys');
        }
        if (!EnableLocRoLanguage) {
            languageList = languageList.filter(lang => lang[0] !== 'ro');
        }
        return languageList.map(([ id, text ]) => ({
            id,
            text,
        }));
    }, [ EnableLocKeysLanguage, EnableLocRoLanguage ]);
}

export function isJapaneseKoreanChineseString(input: string) {
    const JapaneseChineseKoreanRegex = /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Hangul}/u;

    return JapaneseChineseKoreanRegex.test(input);
}
